import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import TransportForm from '../components/TransportForm';
import TransportMap from '../components/TransportMap';
import { Card } from '../components/ui/card';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TransportRequestPage = () => {
  const [searchParams] = useSearchParams();
  const [departure, setDeparture] = useState('');
  const [arrival, setArrival] = useState('');
  const [stops, setStops] = useState([]);
  const [totalDistance, setTotalDistance] = useState(null);
  const [contextNotes, setContextNotes] = useState(null);
  const [duplicateDraft, setDuplicateDraft] = useState(null);

  // Pré-remplissage depuis une duplication d'opération (sessionStorage)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('baobab_operation_draft');
      if (raw) {
        const draft = JSON.parse(raw);
        setDuplicateDraft(draft);
        if (draft.departureAddress) setDeparture(draft.departureAddress);
        if (draft.arrivalAddress) setArrival(draft.arrivalAddress);
        sessionStorage.removeItem('baobab_operation_draft');
      }
    } catch (e) { logger.debug('No draft to restore'); }
  }, []);

  // Pré-remplissage depuis une conversation : ?from_conv=ID
  useEffect(() => {
    const convId = searchParams.get('from_conv');
    if (!convId) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/messages?conversation_id=${convId}`);
        const msgs = res.data || [];
        if (msgs.length === 0) return;
        // Prendre les 3 derniers messages utilisateur (pas les auto-replies du système)
        const userMsgs = msgs.filter(m => m.senderType === 'client').slice(-3);
        if (userMsgs.length === 0) return;
        const summary = userMsgs.map(m => `• ${m.content}`).join('\n');
        setContextNotes(`Contexte (extrait de la conversation #${convId.slice(0, 8)}) :\n${summary}`);
      } catch (e) {
        logger.debug('Could not load conv context:', e?.message);
      }
    })();
  }, [searchParams]);

  const handleAddressChange = (field, value, coords) => {
    if (field === 'departure') {
      setDeparture(value);
    } else if (field === 'arrival') {
      setArrival(value);
    }
  };

  const handleStopsChange = (newStops) => {
    setStops(newStops);
  };

  const handleDistanceCalculated = (distance) => {
    setTotalDistance(distance);
  };

  const handleFormSubmit = (formData) => {
    logger.debug('Transport request submitted:', formData);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl md:text-3xl font-bold text-gray-900">Nouvelle demande de transport</h1>
        <p className="text-gray-500 mt-1">Merci de renseigner les détails ci-dessous pour obtenir une estimation</p>
        {duplicateDraft && (
          <div className="mt-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800" data-testid="duplicate-banner">
            <strong>Opération dupliquée</strong> — les champs ont été pré-remplis. Vérifiez et ajustez avant d'envoyer.
          </div>
        )}
        {contextNotes && (
          <div className="mt-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800" data-testid="conv-context-banner">
            <strong>Contexte importé depuis votre conversation :</strong>
            <pre className="mt-1 whitespace-pre-wrap font-sans">{contextNotes}</pre>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Side - Form */}
        <Card className="p-6">
          <TransportForm
            initialData={duplicateDraft || (contextNotes ? { notes: contextNotes } : undefined)}
            onSubmit={handleFormSubmit}
            onAddressChange={handleAddressChange}
            onStopsChange={handleStopsChange}
          />
        </Card>

        {/* Right Side - Map */}
        <Card className="p-0 overflow-hidden h-[800px]">
          <TransportMap
            departure={departure}
            arrival={arrival}
            stops={stops}
            onDistanceCalculated={handleDistanceCalculated}
          />
        </Card>
      </div>
    </div>
  );
};

export default TransportRequestPage;
